<template>
  <article class="tool-page">
    <nav class="tool-page__breadcrumbs" aria-label="Breadcrumb">
      <router-link to="/console/tools">All tools</router-link>
      <span class="tool-page__breadcrumb-separator">/</span>
      <span>Job-description prompt</span>
    </nav>

    <header class="tool-page__header">
      <h1>Job-description prompt</h1>
      <p class="tool-page__lead">
        A paste-ready prompt that teaches an external LLM agent (Claude, GPT, etc.) how to author
        a beads-conformant GitHub issue or <code>JobSubmission</code> payload. The prompt covers
        nine job archetypes the planner already models, the universal rules every plan is validated
        against, and the JobSubmission schema. Public, no auth, designed for curl-and-paste workflows.
      </p>
    </header>

    <section class="tool-page__section">
      <h2>Quick start</h2>
      <p>Paste this into your agent's chat window, then describe what you want to build:</p>
      <CodeBlock language="bash" :code="quickStartCurl" />
      <p>The response is a self-describing Markdown document. Your agent now has full context
        and produces a complete GitHub issue body or a <code>JobSubmission</code> JSON.</p>
    </section>

    <section class="tool-page__section">
      <h2>Available archetypes</h2>
      <p>
        Every issue you file maps to one of nine archetypes. Each entry below is fetched live from
        the manifest endpoint at
        <code>{{ manifestUrl }}</code> &mdash; if a new archetype ships, it appears here without a
        site redeploy.
      </p>

      <div v-if="manifestState === 'loading'" class="tool-page__placeholder">
        Loading archetypes&hellip;
      </div>
      <div v-else-if="manifestState === 'error'" class="alert alert--error">
        Could not fetch archetypes from <code>{{ manifestUrl }}</code>: {{ manifestError }}
      </div>
      <table v-else class="tool-page__archetypes">
        <thead>
          <tr>
            <th scope="col">Archetype</th>
            <th scope="col">Use when</th>
            <th scope="col">Bead sequence</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in manifest.archetypes" :key="entry.id">
            <td class="tool-page__archetype-id">
              <a :href="archetypeUrl(entry.id)" target="_blank" rel="noopener">
                <code>{{ entry.id }}</code>
              </a>
              <div class="tool-page__archetype-label">{{ entry.label }}</div>
            </td>
            <td>{{ entry.summary }}</td>
            <td>
              <code v-for="(step, i) in entry.beadSequence" :key="i" class="tool-page__bead-step">
                {{ step }}<span v-if="i < entry.beadSequence.length - 1"> &rarr; </span>
              </code>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="tool-page__section">
      <h2>Integration patterns</h2>
      <p>
        Three patterns depending on how much agent-side glue you want. Pattern 1 is zero-glue:
        you, an agent chat, one issue at a time. Pattern 2 is for agents that should pick the
        archetype themselves. Pattern 3 is for automated submission via a script or CI job.
      </p>

      <h3>Pattern 1 &mdash; Manual paste</h3>
      <p>The simplest. Curl the URL, copy the response, paste into your agent.</p>
      <CodeBlock language="bash" :code="pattern1Bash" />
      <p>Then in your agent chat window, paste the file contents and describe the work:</p>
      <CodeBlock language="markdown" :code="pattern1Chat" />
      <p>If you already know the archetype, fetch the focused subset to save tokens:</p>
      <CodeBlock language="bash" :code="pattern1Focused" />

      <h3>Pattern 2 &mdash; Agent-driven navigation</h3>
      <p>
        Tell the agent about the API in its system prompt; let it fetch the manifest, pick the right
        archetype, and pull focused guidance on demand. The manifest's <code>detailUrl</code> for each
        archetype, plus the <code>_links</code> object on every JSON response, give the agent
        everything it needs to navigate.
      </p>
      <CodeBlock language="markdown" :code="pattern2SystemPrompt" />
      <p>A typical back-and-forth:</p>
      <ol class="tool-page__sequence">
        <li>User: "There's a bug where job submissions with long operator notes return 500 instead of 400. Can you write the issue?"</li>
        <li>Agent fetches <code>{{ manifestUrl }}</code>, sees archetypes. Reasons: known symptom, known reproduction &rarr; archetype is <code>defect</code>.</li>
        <li>Agent fetches <code>{{ baseUrl }}/prompt/job-description?archetype=defect</code> for the focused worked example.</li>
        <li>Agent produces a complete defect-shaped issue body with <code>## Symptom</code>, <code>## Reproduction</code>, and acceptance criteria as <code>- [ ]</code> items.</li>
        <li>User reviews and runs <code>gh issue create</code>.</li>
      </ol>

      <h3>Pattern 3 &mdash; Programmatic with structured outputs</h3>
      <p>
        For automated submission, fetch the JSON Schema once and feed it directly to a JSON-Schema-aware
        decoder &mdash; Anthropic Structured Outputs (Nov 2025 beta), <code>instructor</code>, OpenAI's
        <code>response_format</code>, etc. The decoder enforces shape at token-emission time, so the
        agent literally cannot return malformed output.
      </p>
      <CodeBlock language="typescript" :code="pattern3Code" />
    </section>

    <section class="tool-page__section">
      <h2>Endpoints</h2>
      <table class="tool-page__endpoints">
        <thead>
          <tr>
            <th scope="col">Method &amp; path</th>
            <th scope="col">Default Content-Type</th>
            <th scope="col">Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>GET /prompt/job-description</code></td>
            <td><code>text/plain</code></td>
            <td>Full guide covering all 9 archetypes</td>
          </tr>
          <tr>
            <td><code>GET /prompt/job-description?archetype=&lt;id&gt;</code></td>
            <td><code>text/plain</code></td>
            <td>Focused guide + sibling-navigation footer</td>
          </tr>
          <tr>
            <td><code>GET /prompt/job-description?format=json</code></td>
            <td><code>application/json</code></td>
            <td>Envelope <code>{ version, generatedAt, archetype, guidance, schema, _links }</code></td>
          </tr>
          <tr>
            <td><code>GET /prompt/job-description/manifest</code></td>
            <td><code>application/json</code></td>
            <td>Discovery index of every archetype with detailUrl</td>
          </tr>
          <tr>
            <td><code>GET /prompt/job-description/schema.json</code></td>
            <td><code>application/schema+json</code></td>
            <td>JSON Schema 2020-12 for <code>JobSubmission</code></td>
          </tr>
        </tbody>
      </table>
      <p class="tool-page__note">
        Format negotiation precedence: <code>?format=json</code> or <code>?format=text</code> wins;
        otherwise <code>Accept: application/json</code> or <code>Accept: text/plain</code>; default
        <code>text/plain</code>. Every JSON response also carries an HTTP <code>Link:</code> header
        (RFC 8288) with named relations <code>self</code>, <code>manifest</code>,
        <code>fullPrompt</code>, <code>schema</code>, <code>submit</code>.
      </p>
    </section>

    <section class="tool-page__section">
      <h2>Defect responses</h2>
      <p>
        Invalid input returns a structured defect array (or labeled text lines for plain text)
        naming the field, the offending value, and the valid alternatives &mdash; agents can
        self-correct without whack-a-mole retries.
      </p>
      <CodeBlock language="json" :code="defectExample" />
    </section>
  </article>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useOrchestratorBaseUrl } from '@/composables/useOrchestratorBaseUrl.js'
import CodeBlock from '@/components/CodeBlock.vue'

const { orchestratorBaseUrl } = useOrchestratorBaseUrl()

const baseUrl = computed(() => orchestratorBaseUrl.value)
const manifestUrl = computed(() => `${baseUrl.value}/prompt/job-description/manifest`)
const fullPromptUrl = computed(() => `${baseUrl.value}/prompt/job-description`)

const manifest = ref({ archetypes: [] })
const manifestState = ref('loading')
const manifestError = ref('')

function archetypeUrl(id) {
  return `${baseUrl.value}/prompt/job-description?archetype=${id}`
}

const quickStartCurl = computed(() => `curl ${fullPromptUrl.value}`)

const pattern1Bash = computed(() => `curl ${fullPromptUrl.value} > guide.txt`)

const pattern1Chat = `[paste the contents of guide.txt]

Now: I want to file an issue for adding a Cancel button to the
job-detail page that confirms before cancelling. Produce a
complete GitHub issue body I can paste into \`gh issue create\`.`

const pattern1Focused = computed(() => `curl '${baseUrl.value}/prompt/job-description?archetype=feature' > guide.txt`)

const pattern2SystemPrompt = computed(() => `You have access to the beads issue-authoring API at
${baseUrl.value}.

When the user asks to file an issue, fetch:

  1. GET /prompt/job-description/manifest  — JSON list of issue archetypes.
     Each entry has a "summary" starting with "Use when …" and a "detailUrl".
  2. Pick the archetype matching the user's intent.
     If unsure, ask the user to choose.
  3. GET <detailUrl>  — focused guidance for that archetype.
  4. Produce a GitHub issue body OR a JobSubmission JSON ready to
     POST to /jobs.`)

const pattern3Code = computed(() => `import Anthropic from '@anthropic-ai/sdk';

const client = new Anthropic({
  defaultHeaders: { 'anthropic-beta': 'structured-outputs-2025-11-13' },
});

const guideText = await fetch(
  '${baseUrl.value}/prompt/job-description?archetype=defect',
).then(r => r.text());

const jobSchema = await fetch(
  '${baseUrl.value}/prompt/job-description/schema.json',
).then(r => r.json());

const result = await client.messages.create({
  model: 'claude-opus-4-7',
  max_tokens: 2048,
  system: guideText,
  messages: [{ role: 'user', content: userTaskDescription }],
  response_format: { type: 'json_schema', schema: jobSchema },
});

// result.content is guaranteed to match the JobSubmission shape.
const submission = JSON.parse(result.content[0].text);

await fetch('${baseUrl.value}/jobs', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': YOUR_TENANT,
  },
  body: JSON.stringify(submission),
});`)

const defectExample = `{
  "error": "INVALID_ARCHETYPE",
  "message": "Unknown archetype \\"bug\\"",
  "defects": [{
    "fieldPath": "query.archetype",
    "value": "bug",
    "expected": "one of: research-only | repo-audit | defect | feature | …",
    "hint": "Pick the archetype matching your goal. Fetch /prompt/job-description/manifest …"
  }],
  "_links": {
    "self": "/prompt/job-description",
    "manifest": "/prompt/job-description/manifest",
    "fullPrompt": "/prompt/job-description",
    "schema": "/prompt/job-description/schema.json",
    "submit": "/jobs"
  }
}`

onMounted(async () => {
  try {
    const response = await fetch(manifestUrl.value, { headers: { Accept: 'application/json' } })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    manifest.value = await response.json()
    manifestState.value = 'ready'
  } catch (err) {
    manifestError.value = err instanceof Error ? err.message : String(err)
    manifestState.value = 'error'
  }
})
</script>

<style src="@/styles/console.css"></style>
<style src="@/styles/tool-page.css"></style>
<style scoped>
/* Archetype-specific styling. Generic tool-page layout lives in tool-page.css. */
.tool-page__archetypes {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
  margin-top: 12px;
}

.tool-page__archetypes th,
.tool-page__archetypes td {
  text-align: left;
  vertical-align: top;
  padding: 10px 12px;
  border-bottom: 1px solid var(--shell-border);
  line-height: 1.55;
}

.tool-page__archetypes th {
  font-family: var(--shell-mono-font);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
  background: var(--shell-surface-strong);
}

.tool-page__archetype-id a {
  color: var(--shell-text);
  text-decoration: none;
}

.tool-page__archetype-id a:hover {
  text-decoration: underline;
}

.tool-page__archetype-label {
  font-size: 0.78rem;
  color: var(--shell-text-muted);
  margin-top: 2px;
}

.tool-page__bead-step {
  font-size: 0.85rem;
}

.tool-page__sequence {
  margin: 14px 0;
  padding-left: 20px;
  line-height: 1.7;
}

.tool-page__sequence li {
  margin-bottom: 8px;
}
</style>
